import { useQuery } from "@tanstack/react-query";
import api from "@/lib/axios";
import {
  PurchaseByDriver,
  PurchaseByDriverDetail,
  PurchaseByDriverSearchParams,
  PurchaseByDriverTotal,
} from "@/interface/settlement/purchaseByDriver";

// 기사 목록 조회 (좌측 패널 - Page 응답)
// 매출일자 기간 내 거래가 있는 기사만 조회
export const usePurchaseByDriverList = (
  params: Omit<PurchaseByDriverSearchParams, "itemSearchType" | "itemSearch"> & {
    page?: number;
    size?: number;
  }
) => {
  return useQuery<{
    content: PurchaseByDriver[];
    totalElements: number;
    totalPages: number;
    last: boolean;
  }>({
    queryKey: ["purchaseByDriverList", params],
    queryFn: async () => {
      const { data } = await api.get("/sale-slip/purchase-by-driver/drivers", {
        params: {
          startDate: params.startDate,
          endDate: params.endDate,
          company: params.company || "",
          searchType: params.searchType || "",
          search: params.search || "",
          page: params.page || 0,
          size: params.size || 20,
        },
      });
      return data.data;
    },
    enabled: !!params.startDate && !!params.endDate,
  });
};

// 선택된 기사의 거래 내역 조회 (우측 패널)
export const usePurchaseByDriverDetails = (
  driverCode: string,
  params: PurchaseByDriverSearchParams & { page?: number; size?: number }
) => {
  return useQuery<{
    content: PurchaseByDriverDetail[];
    totalElements: number;
    totalPages: number;
    last: boolean;
  }>({
    queryKey: ["purchaseByDriverDetails", driverCode, params],
    queryFn: async () => {
      const { data } = await api.get(`/sale-slip/purchase-by-driver/transactions/${driverCode}`, {
        params: {
          startDate: params.startDate,
          endDate: params.endDate,
          company: params.company || "",
          itemSearchType: params.itemSearchType || "",
          itemSearch: params.itemSearch || "",
          itemExactMatch: params.itemExactMatch || false,
          page: params.page || 0,
          size: params.size || 20,
        },
      });
      return data.data;
    },
    enabled: !!driverCode && !!params.startDate && !!params.endDate,
  });
};

// 선택된 기사의 거래 합계 조회 (Footer용)
export const usePurchaseByDriverTotals = (
  driverCode: string,
  params: PurchaseByDriverSearchParams
) => {
  return useQuery<PurchaseByDriverTotal>({
    queryKey: ["purchaseByDriverTotals", driverCode, params],
    queryFn: async () => {
      const { data } = await api.get(
        `/sale-slip/purchase-by-driver/transactions/${driverCode}/totals`,
        {
          params: {
            startDate: params.startDate,
            endDate: params.endDate,
            company: params.company || "",
            itemSearchType: params.itemSearchType || "",
            itemSearch: params.itemSearch || "",
            itemExactMatch: params.itemExactMatch || false,
          },
        }
      );
      return data.data;
    },
    enabled: !!driverCode && !!params.startDate && !!params.endDate,
  });
};

// 선택된 차량의 전체 거래 내역 조회 (PDF 출력용 - 페이지네이션 없음)
export const fetchAllPurchaseByDriverDetails = async (
  driverCode: string,
  params: PurchaseByDriverSearchParams
): Promise<PurchaseByDriverDetail[]> => {
  const { data } = await api.get(
    `/sale-slip/purchase-by-driver/transactions/${driverCode}/all`,
    {
      params: {
        startDate: params.startDate,
        endDate: params.endDate,
        company: params.company || "",
        itemSearchType: params.itemSearchType || "",
        itemSearch: params.itemSearch || "",
        itemExactMatch: params.itemExactMatch || false,
      },
    }
  );
  return data.data;
};
